import { t as uiText } from '../i18n';
// src/components/ErrorBoundary.tsx

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { errorTracking } from '../utils/errorTracking';
import { performanceMonitor } from '../utils/performanceMonitor';
import { shouldShowDevChrome } from '../utils/embedContext';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  debugInfo?: any;
  showDebugInfo: boolean;
  devFabHover: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, showDebugInfo: false, devFabHover: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // エラーが発生したときに状態を更新
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // デバッグ情報を収集
    const debugInfo = this.collectDebugInfo(error, errorInfo);
    
    // エラートラッキングにエラーを送信
    errorTracking.logReactError(error, errorInfo);
    
    this.setState({ error, errorInfo, debugInfo });

    // iPhone特有の問題かどうかを判定
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

    if (isIOS || isSafari) {
      // iPhoneまたはSafariでのエラーとして特別にマーク
      errorTracking.logCustomError('iOS/Safari specific error', {
        error_message: error.message,
        error_stack: error.stack,
        component_stack: errorInfo.componentStack,
        user_agent: userAgent,
        is_ios: isIOS,
        is_safari: isSafari,
        debug_info: debugInfo
      });
    }
  }

  // デバッグ情報の収集
  private collectDebugInfo(error: Error, errorInfo: ErrorInfo) {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    
    // iOS版本の取得
    let osVersion: string | undefined;
    if (isIOS) {
      const match = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
      if (match) {
        osVersion = `${match[1]}.${match[2]}${match[3] ? '.' + match[3] : ''}`;
      }
    }

    // Safari版本の取得
    let browserVersion: string | undefined;
    if (isSafari) {
      const match = userAgent.match(/Version\/(\d+\.\d+)/);
      if (match) {
        browserVersion = match[1];
      }
    }

    // パフォーマンス情報
    const performanceMetrics = performanceMonitor.getMetrics();

    // メモリ情報
    const memoryInfo = (performance as any).memory ? {
      used: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024),
      total: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024),
      limit: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024)
    } : null;

    // 最近のネットワークエラー
    const networkErrorCount = (window as any).__networkErrorCount || 0;
    const jsErrorCount = (window as any).__jsErrorCount || 0;

    // 現在のページ情報
    const pageInfo = {
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      referrer: document.referrer,
      title: document.title
    };

    // リソース読み込み状況
    const resourceInfo = {
      scripts: document.scripts.length,
      stylesheets: document.styleSheets.length,
      images: document.images.length,
      totalElements: document.querySelectorAll('*').length
    };

    return {
      timestamp: new Date().toISOString(),
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      errorInfo: {
        componentStack: errorInfo.componentStack
      },
      device: {
        userAgent,
        isIOS,
        isSafari,
        osVersion,
        browserVersion,
        platform: navigator.platform,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        maxTouchPoints: navigator.maxTouchPoints
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        colorDepth: window.screen.colorDepth,
        pixelDepth: window.screen.pixelDepth
      },
      window: {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        devicePixelRatio: window.devicePixelRatio,
        scrollX: window.scrollX,
        scrollY: window.scrollY
      },
      memory: memoryInfo,
      performance: performanceMetrics,
      network: {
        errorCount: networkErrorCount,
        connectionType: (navigator as any).connection?.effectiveType || 'unknown',
        downlink: (navigator as any).connection?.downlink || null,
        rtt: (navigator as any).connection?.rtt || null
      },
      errors: {
        jsErrorCount,
        networkErrorCount
      },
      page: pageInfo,
      resources: resourceInfo,
      localStorage: {
        available: (() => {
          try {
            localStorage.setItem('test', 'test');
            localStorage.removeItem('test');
            return true;
          } catch {
            return false;
          }
        })(),
        itemCount: localStorage.length
      },
      sessionStorage: {
        available: (() => {
          try {
            sessionStorage.setItem('test', 'test');
            sessionStorage.removeItem('test');
            return true;
          } catch {
            return false;
          }
        })(),
        itemCount: sessionStorage.length
      }
    };
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private toggleDebugInfo = () => {
    this.setState(prevState => ({ showDebugInfo: !prevState.showDebugInfo }));
  };

  private handleDevToggle = () => {
    if (this.state.hasError) {
      this.setState({ hasError: false, error: undefined, errorInfo: undefined, debugInfo: undefined });
    } else {
      const fakeError = new Error('Dev preview: ErrorBoundary');
      this.setState({
        hasError: true,
        error: fakeError,
        errorInfo: { componentStack: '\n    in DevPreview\n    in ErrorBoundary' },
      });
    }
  };

  private copyDebugInfo = async () => {
    if (!this.state.debugInfo) return;
    
    const debugText = JSON.stringify(this.state.debugInfo, null, 2);
    
    try {
      await navigator.clipboard.writeText(debugText);
      alert(uiText("Copied debug info to the clipboard"));
    } catch (error) {
      // フォールバック: テキストエリアを使用
      const textArea = document.createElement('textarea');
      textArea.value = debugText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(uiText("Copied debug info to the clipboard"));
    }
  };

  private generateReportSummary = () => {
    if (!this.state.debugInfo) return '';
    
    const { debugInfo } = this.state;
    return uiText("\nError report - {{timestamp}}\n\n[Basic info]\n- Error: {{name}}: {{message}}\n- Page: {{pathname}}\n- Device: {{v4}} {{v5}}\n\n[System]\n- OS: {{platform}}\n- Browser: {{v7}}\n- Screen size: {{innerWidth}}x{{innerHeight}}\n- Memory: {{v10}}\n\n[Network]\n- Status: {{v11}}\n- Connection type: {{connectionType}}\n- Network errors: {{networkErrorCount}}\n- JS errors: {{jsErrorCount}}\n\n[Issue details]\n{{v15}}\n{{v16}}\n{{v17}}\n", { timestamp: debugInfo.timestamp, name: debugInfo.error.name, message: debugInfo.error.message, pathname: debugInfo.page.pathname, v4: debugInfo.device.isIOS ? 'iOS' : debugInfo.device.isSafari ? 'Safari' : uiText("Other"), v5: debugInfo.device.osVersion || debugInfo.device.browserVersion || '', platform: debugInfo.device.platform, v7: debugInfo.device.userAgent.split(' ').pop(), innerWidth: debugInfo.window.innerWidth, innerHeight: debugInfo.window.innerHeight, v10: debugInfo.memory ? `${debugInfo.memory.used}MB / ${debugInfo.memory.limit}MB` : 'N/A', v11: debugInfo.device.onLine ? uiText("Online") : uiText("Offline"), connectionType: debugInfo.network.connectionType, networkErrorCount: debugInfo.errors.networkErrorCount, jsErrorCount: debugInfo.errors.jsErrorCount, v15: debugInfo.device.isIOS ? uiText("⚠️ This is an issue on iOS devices") : '', v16: debugInfo.errors.networkErrorCount > 0 ? uiText("⚠️ A network error is occurring") : '', v17: debugInfo.memory && debugInfo.memory.used > debugInfo.memory.limit * 0.8 ? uiText("⚠️ Memory usage is high") : '' }).trim();
  };

  private renderDevFab() {
    if (!shouldShowDevChrome()) return null;
    const { hasError, devFabHover } = this.state;
    return (
      <button
        type="button"
        onClick={this.handleDevToggle}
        onMouseEnter={() => this.setState({ devFabHover: true })}
        onMouseLeave={() => this.setState({ devFabHover: false })}
        aria-label={uiText("Toggle the error screen (for development)")}
        title={hasError ? uiText("Close the error screen (development only)") : uiText("Preview the error screen (development only)")}
        style={{
          position: 'fixed',
          right: 156,
          bottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
          zIndex: 99998,
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: '1px solid rgba(207,174,145,0.45)',
          background: devFabHover
            ? 'linear-gradient(145deg, #ebd9c8 0%, #cfae91 100%)'
            : 'linear-gradient(145deg, #f5f0eb 0%, rgba(207,174,145,0.55) 100%)',
          boxShadow: devFabHover
            ? '0 6px 20px rgba(90,70,55,0.18), 0 2px 8px rgba(139,111,78,0.15)'
            : '0 4px 16px rgba(90,70,55,0.12), 0 2px 6px rgba(139,111,78,0.1)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#5c4a3a',
          transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
          transform: devFabHover ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
          fontFamily: "'Inter', 'BIZ UDPGothic', 'Hiragino Sans', sans-serif",
        }}
      >
        {hasError ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        )}
      </button>
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      return (
        <>
        {this.renderDevFab()}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '24px 20px',
          textAlign: 'center',
          background: 'linear-gradient(160deg, #faf7f4 0%, #f0ebe5 100%)',
          fontFamily: "'Inter', 'BIZ UDPGothic', 'Hiragino Sans', sans-serif",
        }}>
          <style>{`
            @keyframes eb-fade-up {
              from { opacity: 0; transform: translateY(12px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .eb-btn-primary {
              background: #cfae91;
              color: #fff;
              border: none;
              padding: 11px 26px;
              border-radius: 9999px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              letter-spacing: 0.01em;
              font-family: inherit;
              box-shadow: 0 2px 10px rgba(139,111,78,0.18);
              transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
            }
            .eb-btn-primary:hover {
              background: #b89a7a;
              box-shadow: 0 4px 16px rgba(139,111,78,0.28);
              transform: translateY(-1px);
            }
            .eb-btn-secondary {
              background: transparent;
              color: #8b7260;
              border: 1px solid rgba(207,174,145,0.7);
              padding: 10px 24px;
              border-radius: 9999px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              letter-spacing: 0.01em;
              font-family: inherit;
              transition: background 0.2s, border-color 0.2s, transform 0.15s;
            }
            .eb-btn-secondary:hover {
              background: rgba(207,174,145,0.12);
              border-color: #cfae91;
              transform: translateY(-1px);
            }
            .eb-details summary {
              cursor: pointer;
              font-size: 13px;
              font-weight: 500;
              color: #8b7260;
              letter-spacing: 0.01em;
              list-style: none;
              display: flex;
              align-items: center;
              gap: 6px;
              padding: 10px 14px;
              border-radius: 8px;
              transition: background 0.15s;
            }
            .eb-details summary::-webkit-details-marker { display: none; }
            .eb-details summary:hover { background: rgba(207,174,145,0.1); }
          `}</style>

          <div style={{
            maxWidth: '480px',
            width: '100%',
            background: 'rgba(255,252,249,0.95)',
            padding: '44px 40px 40px',
            borderRadius: '20px',
            boxShadow: '0 2px 4px rgba(90,70,55,0.04), 0 16px 48px -12px rgba(90,70,55,0.14)',
            border: '1px solid rgba(207,174,145,0.18)',
            animation: 'eb-fade-up 0.4s ease-out',
          }}>
            {/* アイコン */}
            <div style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'linear-gradient(145deg, #f5f0eb 0%, rgba(207,174,145,0.3) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              color: '#b89a7a',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h1 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#3d3429',
              marginBottom: '10px',
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
            }}>
              {uiText("We're sorry")}
            </h1>

            <p style={{
              fontSize: '14.5px',
              color: '#6b5d52',
              marginBottom: '28px',
              lineHeight: 1.75,
            }}>
              {uiText("An unexpected error occurred.")}<br />
              {uiText("Error information was recorded automatically.")}
            </p>

            {/* iPhoneユーザー向けメッセージ */}
            {isIOS && (
              <div style={{
                background: 'linear-gradient(135deg, #faf6f1 0%, #f5efea 100%)',
                padding: '14px 16px',
                borderRadius: '12px',
                marginBottom: '28px',
                border: '1px solid rgba(207,174,145,0.35)',
                textAlign: 'left',
              }}>
                <p style={{
                  fontSize: '13px',
                  color: '#7a6251',
                  margin: 0,
                  lineHeight: 1.7,
                }}>
                  <span style={{ fontWeight: 600, display: 'block', marginBottom: 4, color: '#5c4a3a' }}>{uiText("For iPhone users")}</span>
                  {uiText("• In Safari settings, confirm that JavaScript is enabled")}<br />
                  {uiText("• If you are in private browsing, try normal mode")}<br />
                  {uiText("• Update iOS to the latest version")}
                </p>
              </div>
            )}

            {/* アクションボタン */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="eb-btn-primary" onClick={this.handleReload}>
                {uiText("Reload")}
              </button>
              <button className="eb-btn-secondary" onClick={this.handleGoHome}>
                {uiText("Return home")}
              </button>
            </div>

            {/* デバッグ情報セクションを一時的に非表示 */}
            {false && this.state.debugInfo && this.state.showDebugInfo && (
              <div style={{
                marginTop: '28px',
                textAlign: 'left',
                background: 'rgba(245,240,235,0.6)',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid rgba(207,174,145,0.2)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#3d3429', letterSpacing: '-0.01em' }}>
                    {uiText("Detailed error information")}
                  </h3>
                  <button
                    onClick={this.copyDebugInfo}
                    style={{
                      background: 'transparent',
                      color: '#8b7260',
                      border: '1px solid rgba(207,174,145,0.5)',
                      padding: '5px 12px',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontFamily: 'inherit',
                    }}
                  >
                    {uiText("Copy all data")}
                  </button>
                </div>

                <div style={{
                  background: '#fff',
                  padding: '14px 16px',
                  borderRadius: '10px',
                  marginBottom: '12px',
                  border: '1px solid rgba(207,174,145,0.2)',
                }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: 600, color: '#6b5d52', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                    {uiText("Problem report")}
                  </h4>
                  <pre style={{
                    fontSize: '11.5px',
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    color: '#5c4a3a',
                    lineHeight: 1.6,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}>
                    {this.generateReportSummary()}
                  </pre>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(this.generateReportSummary());
                        alert(uiText("Report copied to the clipboard"));
                      } catch {
                        const textArea = document.createElement('textarea');
                        textArea.value = this.generateReportSummary();
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        alert(uiText("Report copied to the clipboard"));
                      }
                    }}
                    style={{
                      background: 'transparent',
                      color: '#8b7260',
                      border: '1px solid rgba(207,174,145,0.5)',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: '11px',
                      cursor: 'pointer',
                      marginTop: '10px',
                      fontFamily: 'inherit',
                    }}
                  >
                    {uiText("Copy this report")}
                  </button>
                </div>

                <details className="eb-details" style={{ marginBottom: '6px' }}>
                  <summary>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
                    {uiText("Device and browser info")}
                  </summary>
                  <pre style={{ fontSize: '11px', overflow: 'auto', background: '#fff', padding: '12px', borderRadius: '8px', border: '1px solid rgba(207,174,145,0.2)', margin: '8px 0 0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#5c4a3a', lineHeight: 1.5 }}>
                    {JSON.stringify({ device: this.state.debugInfo.device, screen: this.state.debugInfo.screen, window: this.state.debugInfo.window }, null, 2)}
                  </pre>
                </details>

                <details className="eb-details" style={{ marginBottom: '6px' }}>
                  <summary>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 6l5 6-5 6"/><path d="M23 6l-5 6 5 6"/></svg>
                    {uiText("Network and performance info")}
                  </summary>
                  <pre style={{ fontSize: '11px', overflow: 'auto', background: '#fff', padding: '12px', borderRadius: '8px', border: '1px solid rgba(207,174,145,0.2)', margin: '8px 0 0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#5c4a3a', lineHeight: 1.5 }}>
                    {JSON.stringify({ network: this.state.debugInfo.network, errors: this.state.debugInfo.errors, memory: this.state.debugInfo.memory, performance: this.state.debugInfo.performance }, null, 2)}
                  </pre>
                </details>

                <details className="eb-details">
                  <summary>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {uiText("Error details")}
                  </summary>
                  <pre style={{ fontSize: '11px', overflow: 'auto', background: '#fff', padding: '12px', borderRadius: '8px', border: '1px solid rgba(207,174,145,0.2)', margin: '8px 0 0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#5c4a3a', lineHeight: 1.5 }}>
                    {JSON.stringify({ error: this.state.debugInfo.error, errorInfo: this.state.debugInfo.errorInfo, page: this.state.debugInfo.page, resources: this.state.debugInfo.resources }, null, 2)}
                  </pre>
                </details>
              </div>
            )}

            {/* 開発環境でのエラー詳細表示 */}
            {import.meta.env.MODE === 'development' && this.state.error && (
              <details className="eb-details" style={{
                marginTop: '24px',
                textAlign: 'left',
                background: 'rgba(245,240,235,0.6)',
                borderRadius: '12px',
                border: '1px solid rgba(207,174,145,0.2)',
                overflow: 'hidden',
              }}>
                <summary style={{ padding: '12px 16px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                  {uiText("Error details (development)")}
                </summary>
                <div style={{ padding: '0 14px 14px' }}>
                  <pre style={{
                    fontSize: '11.5px',
                    overflow: 'auto',
                    background: '#fff',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid rgba(207,174,145,0.2)',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    color: '#5c4a3a',
                    lineHeight: 1.6,
                    margin: 0,
                  }}>
                    {this.state.error.stack}
                  </pre>
                  {this.state.errorInfo && (
                    <pre style={{
                      fontSize: '11.5px',
                      overflow: 'auto',
                      background: '#fff',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      border: '1px solid rgba(207,174,145,0.2)',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      color: '#5c4a3a',
                      lineHeight: 1.6,
                      margin: '8px 0 0',
                    }}>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
        </>
      );
    }

    return (
      <>
        {this.props.children}
        {this.renderDevFab()}
      </>
    );
  }
}

export default ErrorBoundary;
